import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  "Readiness ka matlab kya hai?",
  "मैच के दिन खिलाड़ी को फ्रेश कैसे रखें?",
  "How do I add today's session?",
];

export default function HelpBot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useMemo(() => `help_${Math.random().toString(36).slice(2, 10)}`, []);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const ask = async (text) => {
    const message = (text ?? q).trim();
    if (!message || busy) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", text: message }]);
    setBusy(true);
    try {
      let step = null;
      try { step = localStorage.getItem("xps.tour.step"); } catch { /* ignore */ }
      const { data } = await api.post("/help/chat", { message, session_id: sid, step });
      setMsgs((m) => [...m, { role: "bot", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "bot", text: e?.response?.data?.detail || "The help assistant is unavailable right now." }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button data-testid="helpbot-open" aria-label="Open help" onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-[70] w-12 h-12 bg-foreground text-background flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
          <MessageCircle className="w-5 h-5" />
        </button>
      )}
      {open && (
        <div data-testid="helpbot-panel"
          className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-[70] w-full sm:w-[22rem] max-w-full h-[70vh] sm:h-[28rem] bg-background border border-border shadow-2xl flex flex-col">
          <div className="h-11 border-b border-border flex items-center gap-2 px-3 shrink-0">
            <MessageCircle className="w-4 h-4" />
            <span className="text-xs tracking-[0.18em] uppercase font-bold">Help</span>
            <span className="text-[10px] text-muted-foreground">ask in English, हिन्दी or Hinglish</span>
            <button data-testid="helpbot-close" aria-label="Close help" onClick={() => setOpen(false)} className="ml-auto text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs">Ask anything about using the product. Not medical or training advice.</p>
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="helpbot-suggestion" onClick={() => ask(s)}
                    className="block w-full text-left text-xs border border-border px-3 py-2 hover:bg-surface transition-colors">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`helpbot-msg-${m.role}`}
                className={m.role === "user" ? "ml-6 bg-surface border border-border p-2.5" : "mr-2 border-l-2 border-foreground pl-3"}>
                {m.text}
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="border-t border-border p-2 flex gap-2 shrink-0">
            <input data-testid="helpbot-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="How do I…" className="inp text-sm" />
            <button data-testid="helpbot-send" type="submit" disabled={busy || !q.trim()}
              className="px-3 bg-foreground text-background disabled:opacity-30"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
